import React from "react";
import { Box, useColorModeValue } from "@chakra-ui/react";
import { motion, useReducedMotion } from "framer-motion";

const MotionBox = motion(Box);

function FloatingOrb({
  size,
  top,
  left,
  right,
  bottom,
  bg,
  blur,
  duration,
  delay,
  moveX,
  moveY,
}) {
  const reduceMotion = useReducedMotion();
  return (
    <MotionBox
      position="absolute"
      w={size}
      h={size}
      top={top}
      left={left}
      right={right}
      bottom={bottom}
      borderRadius="full"
      bg={bg}
      filter={`blur(${blur}px)`}
      opacity={0.42}
      pointerEvents="none"
      zIndex={0}
      animate={
        reduceMotion
          ? undefined
          : {
              x: [0, moveX, 0, -moveX * 0.4, 0],
              y: [0, -moveY, moveY * 0.5, 0],
              scale: [1, 1.06, 0.98, 1],
            }
      }
      transition={{
        duration,
        repeat: Infinity,
        ease: "easeInOut",
        delay,
      }}
    />
  );
}

/**
 * Full-viewport soft 3D depth: gradient field + slow parallax orbs.
 * Sits behind public layout content (fixed, pointer-events none).
 */
export default function PublicAmbient3D() {
  const mesh = useColorModeValue(
    "linear-gradient(168deg, #ecfeff 0%, #f8fafc 42%, #eff6ff 78%, #f0fdfa 100%)",
    "linear-gradient(168deg, #0c1220 0%, #0f172a 35%, #134e4a 62%, #0f172a 100%)"
  );
  const grid = useColorModeValue(
    "linear-gradient(rgba(15, 118, 110, 0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(15, 118, 110, 0.07) 1px, transparent 1px)",
    "linear-gradient(rgba(94, 234, 212, 0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(94, 234, 212, 0.06) 1px, transparent 1px)"
  );
  const o1 = useColorModeValue("rgba(45, 212, 191, 0.45)", "rgba(45, 212, 191, 0.35)");
  const o2 = useColorModeValue("rgba(99, 102, 241, 0.28)", "rgba(129, 140, 248, 0.25)");
  const o3 = useColorModeValue("rgba(14, 165, 233, 0.22)", "rgba(56, 189, 248, 0.2)");
  const vignette = useColorModeValue(
    "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.04) 100%)",
    "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.32) 100%)"
  );

  return (
    <Box
      position="fixed"
      inset={0}
      zIndex={0}
      pointerEvents="none"
      overflow="hidden"
      aria-hidden
    >
      <Box position="absolute" inset={0} bg={mesh} />
      <Box
        position="absolute"
        inset={0}
        opacity={0.35}
        bgImage={grid}
        bgSize="48px 48px"
        transform="perspective(1200px) rotateX(78deg) scale(2.2)"
        transformOrigin="center top"
      />
      <FloatingOrb
        size="min(68vw, 520px)"
        top="-12%"
        left="-18%"
        bg={o1}
        blur={90}
        duration={22}
        delay={0}
        moveX={28}
        moveY={22}
      />
      <FloatingOrb
        size="min(52vw, 420px)"
        top="18%"
        right="-14%"
        bg={o2}
        blur={80}
        duration={26}
        delay={1.2}
        moveX={-32}
        moveY={26}
      />
      <FloatingOrb
        size="min(48vw, 380px)"
        bottom="-8%"
        left="8%"
        bg={o3}
        blur={72}
        duration={20}
        delay={0.6}
        moveX={24}
        moveY={-20}
      />
      <Box position="absolute" inset={0} bg={vignette} />
    </Box>
  );
}
